// frontend/src/pages/reports/TrialBalance.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./TrialBalance.css";

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }
  return currency;
};

/* ============================================================
   INLINE STYLES — self-contained, no external CSS required
============================================================ */
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "13.5px",
  color: "#1e293b",
};

const theadRowStyle = {
  background: "#f3f4f6",
};

const thBase = {
  padding: "12px 14px",
  textAlign: "left",
  fontWeight: 700,
  color: "#170f48",
  fontSize: "11.5px",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  borderBottom: "2px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const thRight = {
  ...thBase,
  textAlign: "right",
};

const thCenter = {
  ...thBase,
  textAlign: "center",
};

const tdBase = {
  padding: "12px 14px",
  borderBottom: "1px solid #eef1f5",
  verticalAlign: "middle",
};

const tdRight = {
  ...tdBase,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

const tdCenter = {
  ...tdBase,
  textAlign: "center",
  color: "#94a3b8",
  fontWeight: 600,
};

const totalRowStyle = {
  background: "#f8fafc",
  fontWeight: 700,
};

const totalCellLeft = {
  padding: "14px",
  textAlign: "right",
  fontWeight: 750,
  color: "#170f48",
  borderTop: "2px solid #cbd5e1",
  borderBottom: "2px solid #cbd5e1",
};

const totalCellRight = {
  padding: "14px",
  textAlign: "right",
  fontWeight: 800,
  fontSize: "14.5px",
  color: "#170f48",
  borderTop: "2px solid #cbd5e1",
  borderBottom: "2px solid #cbd5e1",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

const typeBadgeStyle = (type) => {
  const colors = {
    asset: { bg: "#e6f7ff", fg: "#096dd9" },
    liability: { bg: "#fff1f0", fg: "#cf1322" },
    equity: { bg: "#f9f0ff", fg: "#722ed1" },
    income: { bg: "#f6ffed", fg: "#389e0d" },
    expense: { bg: "#fff7e6", fg: "#d46b08" },
  };
  const key = String(type || "").toLowerCase();
  const c = colors[key] || { bg: "#f0f2f5", fg: "#475569" };
  return {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 999,
    background: c.bg,
    color: c.fg,
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    whiteSpace: "nowrap",
  };
};

const refreshButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 18px",
  background: "#170f48",
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 2px 6px rgba(23,15,72,0.15)",
  minWidth: "120px",
  justifyContent: "center",
};

const printButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 18px",
  background: "#f1592a",
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(241,89,42,0.35)",
  minWidth: "140px",
  justifyContent: "center",
};

export default function TrialBalance() {
  const { company: authCompany } = useAuth();
  const { formatCurrency } = useCurrency();

  // ✅ NEW: full company (with address, phones 2 & 3, email)
  const [fullCompany, setFullCompany] = useState(null);

  // ✅ NEW: load the full company once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${url}/api/company/current`, {
          headers: getAuthHeaders(),
          timeout: 5000,
        });
        if (!cancelled && res?.data) setFullCompany(res.data);
      } catch {
        if (!cancelled) setFullCompany(authCompany || null);
      }
    })();
    return () => { cancelled = true; };
  }, [authCompany]);

  // ✅ Use full company, falling back to auth company while loading
  const company = fullCompany || authCompany;

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const formatCurrencyLocal = useCallback(
    (value) => {
      if (value === undefined || value === null) value = 0;
      const currencyCode = companyCurrency;
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value));
      } catch {
        return `${currencyCode} ${Number(value).toFixed(2)}`;
      }
    },
    [companyCurrency]
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totals, setTotals] = useState({
    debit: 0,
    credit: 0,
    difference: 0,
    balanced: true,
  });

  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [accountType, setAccountType] = useState("");

  const loadTrialBalance = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = {};
      if (asOfDate) params.as_of_date = asOfDate;
      if (accountType) params.account_type = accountType;

      const response = await axios.get(`${url}/api/reports/trial-balance`, {
        headers: getAuthHeaders(),
        params,
      });

      const responseRows = response.data?.data || [];
      const responseTotals = response.data?.totals || {
        debit: 0,
        credit: 0,
        difference: 0,
        balanced: true,
      };

      setRows(responseRows);
      setTotals({
        debit: Number(responseTotals.debit || 0),
        credit: Number(responseTotals.credit || 0),
        difference: Number(responseTotals.difference || 0),
        balanced: Boolean(responseTotals.balanced),
      });
    } catch (err) {
      setRows([]);
      setTotals({ debit: 0, credit: 0, difference: 0, balanced: true });
      setError(
        err?.response?.data?.message ||
          "Unable to load the trial balance. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [asOfDate, accountType]);

  useEffect(() => {
    loadTrialBalance();
  }, [loadTrialBalance]);

  const formattedAsOfDate = asOfDate
    ? new Date(`${asOfDate}T00:00:00`).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "All Dates";

  const fiscalYearLabel = company?.fiscal_year_start
    ? new Date(company.fiscal_year_start).getFullYear()
    : new Date().getFullYear();

  const logoUrl = company?.logo
    ? company.logo.startsWith("http")
      ? company.logo
      : `${url}/${company.logo.replace(/^\/+/, "")}`
    : null;

  // ✅ Address + Phone 1 · 2 · 3 (from /api/company/current)
  const companyAddress = company?.address || "";
  const companyContact = company?.contact || "";
  const companyContact2 = company?.contact_2 || "";
  const companyContact3 = company?.contact_3 || "";
  const companyEmail = company?.admin_email || "";
  const phoneLine = [companyContact, companyContact2, companyContact3]
    .filter(Boolean)
    .join(" · ");

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="trial-balance-page">
      {/* =========================================================
          SCREEN-ONLY CONTROLS
      ========================================================= */}
      <div className="screen-only trial-balance-container">
        {/* Header */}
        <div className="report-header">
          <div>
            <p className="report-eyebrow">FINANCIAL REPORTS</p>
            <h1 className="report-title">Trial Balance</h1>
            <p className="report-subtitle">
              Review account debit and credit balances as of the selected date.
            </p>
          </div>

          <div
            className={`balance-status ${
              totals.balanced ? "status-balanced" : "status-unbalanced"
            }`}
          >
            <span className="status-dot" />
            <div>
              <span className="status-label">Status</span>
              <strong>{totals.balanced ? "Balanced" : "Out of Balance"}</strong>
            </div>
          </div>
        </div>

        {/* Filters + action buttons */}
        <div className="filter-card">
          <div className="filter-fields">
            <div className="filter-group">
              <label htmlFor="trial-date">As of Date</label>
              <input
                id="trial-date"
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="account-type">Account Type</label>
              <select
                id="account-type"
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
              >
                <option value="">All Account Types</option>
                <option value="Asset">Asset</option>
                <option value="Liability">Liability</option>
                <option value="Equity">Equity</option>
                <option value="Income">Income</option>
                <option value="Expense">Expense</option>
              </select>
            </div>

            <button
              type="button"
              onClick={loadTrialBalance}
              disabled={loading}
              style={refreshButtonStyle}
            >
              <span style={{ fontSize: "16px", lineHeight: 1 }}>↻</span>
              {loading ? "Refreshing..." : "Refresh"}
            </button>

            <button
              type="button"
              onClick={handlePrint}
              disabled={loading || rows.length === 0}
              style={{
                ...printButtonStyle,
                opacity: loading || rows.length === 0 ? 0.6 : 1,
                cursor: loading || rows.length === 0 ? "not-allowed" : "pointer",
              }}
              title="Print or save as A4 PDF"
            >
              <span style={{ fontSize: "16px", lineHeight: 1 }}>🖨️</span>
              Print (A4)
            </button>
          </div>

          <div className="records-count">
            <span className="records-icon">▤</span>
            <span>
              Showing <strong>{rows.length}</strong>{" "}
              {rows.length === 1 ? "account" : "accounts"}
            </span>
          </div>
        </div>

        {/* Summary cards */}
        <div className="summary-grid">
          <div className="summary-card debit-summary">
            <div className="summary-icon">DR</div>
            <div>
              <span className="summary-label">Total Debit</span>
              <h3>{formatCurrencyLocal(totals.debit)}</h3>
            </div>
          </div>

          <div className="summary-card credit-summary">
            <div className="summary-icon">CR</div>
            <div>
              <span className="summary-label">Total Credit</span>
              <h3>{formatCurrencyLocal(totals.credit)}</h3>
            </div>
          </div>

          <div className="summary-card accounts-summary">
            <div className="summary-icon">#</div>
            <div>
              <span className="summary-label">Accounts</span>
              <h3>{rows.length}</h3>
            </div>
          </div>

          <div
            className={`summary-card ${
              totals.balanced ? "balance-summary" : "difference-summary"
            }`}
          >
            <div className="summary-icon">{totals.balanced ? "✓" : "!"}</div>
            <div>
              <span className="summary-label">
                {totals.balanced ? "Balance Check" : "Difference"}
              </span>
              <h3>
                {totals.balanced
                  ? "Balanced"
                  : formatCurrencyLocal(Math.abs(totals.difference))}
              </h3>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <div className="error-icon">!</div>
            <div>
              <strong>Unable to load report</strong>
              <span>{error}</span>
            </div>
            <button type="button" onClick={loadTrialBalance}>
              Try Again
            </button>
          </div>
        )}

        {/* Table card */}
        <div className="table-card">
          <div className="table-card-header">
            <div>
              <h2>Account Balances</h2>
              <p>Debit and credit balances for all matching ledger accounts.</p>
            </div>
            <span className="table-count">
              {rows.length} {rows.length === 1 ? "Record" : "Records"}
            </span>
          </div>

          <div className="table-wrapper" style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={theadRowStyle}>
                  <th style={{ ...thCenter, width: "60px" }}>No.</th>
                  <th style={{ ...thBase, width: "120px" }}>Account Code</th>
                  <th style={thBase}>Account Name</th>
                  <th style={{ ...thCenter, width: "130px" }}>Account Type</th>
                  <th style={{ ...thRight, width: "150px" }}>Debit (DR)</th>
                  <th style={{ ...thRight, width: "150px" }}>Credit (CR)</th>
                  <th style={{ ...thBase, minWidth: "200px" }}>Explanation</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 7 }).map((_, i) => (
                    <tr key={`skeleton-${i}`}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} style={tdBase}>
                          <span
                            style={{
                              display: "inline-block",
                              height: "14px",
                              width: j === 2 ? "70%" : "60%",
                              borderRadius: "4px",
                              background:
                                "linear-gradient(90deg, #eef1f5 25%, #e2e6ec 50%, #eef1f5 75%)",
                              backgroundSize: "200% 100%",
                              animation: "tbSkeleton 1.4s ease-in-out infinite",
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ ...tdBase, padding: "48px 16px", textAlign: "center" }}>
                      <div style={{ fontSize: "40px", marginBottom: "12px", color: "#cbd5e1" }}>▤</div>
                      <h3 style={{ margin: "0 0 6px 0", color: "#170f48" }}>No accounts found</h3>
                      <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>
                        No account balances were found for the selected filters.
                      </p>
                    </td>
                  </tr>
                ) : (
                  <>
                    {rows.map((row, index) => {
                      const debit = Number(row.debit || 0);
                      const credit = Number(row.credit || 0);
                      return (
                        <tr
                          key={row.account_id || `row-${index}`}
                          style={{
                            background: index % 2 === 0 ? "#ffffff" : "#fafbfc",
                          }}
                        >
                          <td style={tdCenter}>{index + 1}</td>
                          <td style={{ ...tdBase, fontWeight: 700, color: "#170f48" }}>
                            {row.account_code || "-"}
                          </td>
                          <td style={{ ...tdBase, fontWeight: 600, color: "#1e293b" }}>
                            {row.account_name || "Unknown Account"}
                          </td>
                          <td style={{ ...tdBase, textAlign: "center" }}>
                            <span style={typeBadgeStyle(row.account_type)}>
                              {row.account_type || "Unknown"}
                            </span>
                          </td>
                          <td
                            style={{
                              ...tdRight,
                              color: debit > 0 ? "#0f766e" : "#cbd5e1",
                              fontWeight: debit > 0 ? 600 : 400,
                            }}
                          >
                            {debit > 0 ? formatCurrencyLocal(debit) : "—"}
                          </td>
                          <td
                            style={{
                              ...tdRight,
                              color: credit > 0 ? "#b91c1c" : "#cbd5e1",
                              fontWeight: credit > 0 ? 600 : 400,
                            }}
                          >
                            {credit > 0 ? formatCurrencyLocal(credit) : "—"}
                          </td>
                          <td style={{ ...tdBase, color: "#64748b", fontSize: "12.5px" }}>
                            {row.explanation || "—"}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Totals row */}
                    <tr style={totalRowStyle}>
                      <td colSpan="4" style={totalCellLeft}>
                        TOTAL · Trial Balance
                      </td>
                      <td style={totalCellRight}>{formatCurrencyLocal(totals.debit)}</td>
                      <td style={totalCellRight}>{formatCurrencyLocal(totals.credit)}</td>
                      <td
                        style={{
                          padding: "14px",
                          borderTop: "2px solid #cbd5e1",
                          borderBottom: "2px solid #cbd5e1",
                          color: totals.balanced ? "#15803d" : "#b91c1c",
                          fontWeight: 700,
                          fontSize: "13px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {totals.balanced
                          ? "✓ Debits equal Credits"
                          : `! Difference: ${formatCurrencyLocal(
                              Math.abs(totals.difference)
                            )}`}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Success box */}
        {!loading && rows.length > 0 && (
          <div
            className={`balance-success-box ${
              totals.balanced ? "success-ok" : "success-bad"
            }`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              marginTop: "18px",
              padding: "16px 20px",
              borderRadius: "12px",
              background: totals.balanced ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${totals.balanced ? "#bbf7d0" : "#fecaca"}`,
              color: totals.balanced ? "#15803d" : "#b91c1c",
            }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                background: totals.balanced ? "#15803d" : "#b91c1c",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {totals.balanced ? "✓" : "!"}
            </div>
            <div>
              {totals.balanced ? (
                <>
                  <strong style={{ display: "block", fontSize: "15px" }}>
                    Debits Equal Credits: {formatCurrencyLocal(totals.debit)} ={" "}
                    {formatCurrencyLocal(totals.credit)}
                  </strong>
                  <span style={{ fontSize: "12.5px", opacity: 0.85 }}>
                    The accounting equation is in balance.
                  </span>
                </>
              ) : (
                <>
                  <strong style={{ display: "block", fontSize: "15px" }}>
                    Out of Balance by {formatCurrencyLocal(Math.abs(totals.difference))}
                  </strong>
                  <span style={{ fontSize: "12.5px", opacity: 0.85 }}>
                    Debits ({formatCurrencyLocal(totals.debit)}) do not equal
                    Credits ({formatCurrencyLocal(totals.credit)}).
                  </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* =========================================================
          PRINT-ONLY A4 REPORT
      ========================================================= */}
      <div className="print-only print-page">
        {/* Company header */}
        <div className="print-header">
          <div className="print-header-left">
            {logoUrl && (
              <img src={logoUrl} alt="Company Logo" className="print-logo" />
            )}
            <div className="print-header-text">
              <h1 className="print-company-name">
                {company?.company_name || "Company Name"}
              </h1>

              {/* ✅ Address */}
              <p className="print-company-line">
                {companyAddress || "No address"}
              </p>

              {/* ✅ Phone 1 · Phone 2 · Phone 3 */}
              {phoneLine && (
                <p className="print-company-line">Tel: {phoneLine}</p>
              )}

              <p className="print-company-line">
                {companyEmail ? `Email: ${companyEmail}` : ""}
                {company?.company_code ? ` · Code: ${company.company_code}` : ""}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{formattedAsOfDate}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">
                {fiscalYearLabel}-{fiscalYearLabel + 1}
              </span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{companyCurrency}</span>
            </div>
          </div>
        </div>

        {/* Title block */}
        <div className="print-title-block">
          <h2 className="print-report-title">TRIAL BALANCE</h2>
          <p className="print-report-subtitle">
            As of {formattedAsOfDate}
            {accountType ? ` · ${accountType} Accounts Only` : ""}
          </p>
        </div>

        {/* Print table */}
        <table className="print-table">
          <thead>
            <tr>
              <th className="print-col-no">No.</th>
              <th className="print-col-code">Code</th>
              <th className="print-col-name">Account Name</th>
              <th className="print-col-type">Type</th>
              <th className="print-col-money">Debit (DR)</th>
              <th className="print-col-money">Credit (CR)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="6" className="print-empty">
                  No account balances found for the selected filters.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                const debit = Number(row.debit || 0);
                const credit = Number(row.credit || 0);
                return (
                  <tr key={row.account_id || `print-${index}`}>
                    <td className="print-col-no">{index + 1}</td>
                    <td className="print-col-code">{row.account_code || "-"}</td>
                    <td className="print-col-name">
                      {row.account_name || "Unknown Account"}
                    </td>
                    <td className="print-col-type">{row.account_type || "-"}</td>
                    <td className="print-col-money">
                      {debit > 0 ? formatCurrencyLocal(debit) : ""}
                    </td>
                    <td className="print-col-money">
                      {credit > 0 ? formatCurrencyLocal(credit) : ""}
                    </td>
                  </tr>
                );
              })
            )}
            {rows.length > 0 && (
              <tr className="print-total-row">
                <td colSpan="4" className="print-total-label">
                  TOTAL
                </td>
                <td className="print-col-money print-total-value">
                  {formatCurrencyLocal(totals.debit)}
                </td>
                <td className="print-col-money print-total-value">
                  {formatCurrencyLocal(totals.credit)}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Balance status */}
        {rows.length > 0 && (
          <div className="print-status">
            {totals.balanced ? (
              <p className="print-status-ok">
                ✓ Debits equal credits. Trial balance is balanced.
              </p>
            ) : (
              <p className="print-status-bad">
                ! Out of balance by {formatCurrencyLocal(Math.abs(totals.difference))}.
              </p>
            )}
          </div>
        )}

        {/* Signature footer */}
        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      {/* =========================================================
          INLINE STYLES — self-contained
      ========================================================= */}
      <style>{`
        .print-only { display: none; }

        @keyframes tbSkeleton {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        @page {
          size: A4 portrait;
          margin: 12mm 14mm 14mm 14mm;
        }

        @media print {
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 10pt;
          }

          body * {
            visibility: hidden !important;
          }
          .print-only,
          .print-only * {
            visibility: visible !important;
          }
          .print-only {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            display: block !important;
            padding: 0 !important;
            margin: 0 !important;
            background: #ffffff !important;
          }
          .screen-only {
            display: none !important;
          }

          .print-page {
            font-family: "Helvetica Neue", Arial, sans-serif;
            color: #111111;
            padding: 0;
            max-width: 100%;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10mm;
            padding-bottom: 4mm;
            border-bottom: 1.5pt solid #222222;
            margin-bottom: 5mm;
          }
          .print-header-left {
            display: flex;
            align-items: center;
            gap: 6mm;
            flex: 1;
          }
          .print-logo {
            max-height: 20mm;
            max-width: 28mm;
            object-fit: contain;
          }
          .print-company-name {
            font-size: 15pt;
            font-weight: 700;
            color: #170f48;
            margin: 0 0 1mm 0;
            letter-spacing: 0.3pt;
          }
          .print-company-line {
            font-size: 8.5pt;
            color: #444444;
            margin: 0.4mm 0;
          }
          .print-header-right {
            text-align: right;
            font-size: 8.5pt;
          }
          .print-meta-item {
            display: flex;
            justify-content: flex-end;
            gap: 4mm;
            margin-bottom: 1.2mm;
          }
          .print-meta-label {
            color: #666666;
            font-weight: 500;
          }
          .print-meta-value {
            color: #111111;
            font-weight: 700;
            min-width: 28mm;
            text-align: right;
          }

          .print-title-block {
            text-align: center;
            margin-bottom: 5mm;
          }
          .print-report-title {
            font-size: 14pt;
            font-weight: 700;
            letter-spacing: 2pt;
            color: #170f48;
            margin: 0 0 1mm 0;
          }
          .print-report-subtitle {
            font-size: 9.5pt;
            color: #555555;
            margin: 0;
          }

          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9pt;
            page-break-inside: auto;
          }
          .print-table thead {
            display: table-header-group;
          }
          .print-table tr {
            page-break-inside: avoid;
          }
          .print-table th,
          .print-table td {
            border: 0.5pt solid #999999;
            padding: 3.5pt 5pt;
            vertical-align: middle;
          }
          .print-table thead th {
            background: #eeeeee !important;
            color: #000000 !important;
            font-weight: 700;
            text-align: left;
            font-size: 8.5pt;
            text-transform: uppercase;
            letter-spacing: 0.3pt;
          }
          .print-col-no {
            width: 8mm;
            text-align: center;
          }
          .print-col-code {
            width: 22mm;
          }
          .print-col-type {
            width: 22mm;
          }
          .print-col-money {
            width: 28mm;
            text-align: right;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }
          .print-total-row {
            background: #f4f4f4 !important;
            font-weight: 700;
          }
          .print-total-label {
            text-align: right;
            letter-spacing: 0.5pt;
          }
          .print-total-value {
            font-weight: 700;
          }
          .print-empty {
            text-align: center;
            padding: 12pt;
            font-style: italic;
          }

          .print-status {
            margin-top: 4mm;
            text-align: center;
            font-size: 9pt;
          }
          .print-status-ok {
            color: #1e7e34;
            font-weight: 700;
            margin: 0;
          }
          .print-status-bad {
            color: #c82333;
            font-weight: 700;
            margin: 0;
          }

          .print-footer {
            margin-top: 10mm;
          }
          .print-footer-row {
            display: flex;
            justify-content: space-between;
            gap: 8mm;
            margin-bottom: 6mm;
          }
          .print-signature {
            flex: 1;
            text-align: center;
            font-size: 8.5pt;
            color: #333333;
          }
          .print-signature-line {
            border-top: 0.5pt solid #333333;
            margin-bottom: 1.5mm;
            height: 10mm;
          }
          .print-footer-note {
            text-align: center;
            font-size: 7.5pt;
            color: #888888;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
}
